"use client";

import * as React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslations } from "next-intl";
import {
  Avatar,
  Badge,
  Button,
  Dialog,
  Select,
  Spinner,
  useToast,
} from "@chotca/ui";
import {
  useCreateSourcedInvite,
  useMe,
  usePlanStatus,
  useOrgPostings,
  useTalentFeed,
  useTalentProfile,
} from "@chotca/api-client";
import {
  EmployerAnalyticsEvent,
  WORKER_POSITION_LABEL_VI,
  type TalentCard,
  type TalentCardExperience,
  type WorkerPosition,
} from "@chotca/config";
import { ChotcaApiError } from "@chotca/sdk";
import {
  ArrowLeft,
  Briefcase,
  Check,
  ChevronRight,
  MapPin,
  Star,
  X,
} from "lucide-react";
import { logAnalyticsEvent } from "@/lib/analytics";
import { SlotCalendar } from "@/components/employer/slot-calendar";
import { ProfileBody } from "@/components/worker-profile-modal";

const MAX_PICK = 5;

/**
 * "Tìm ứng viên" — full-screen deck where the employer browses open-to-work
 * workers and invites them into an OPEN posting (docs/01-product.md). The card surfaces
 * the two signals an F&B employer scans first (experience + bio); tapping it
 * opens the full CV in the same modal used for applicants. Full-screen so the
 * mobile bottom nav is hidden during the focused sourcing flow.
 */
export function CandidatesClient() {
  const me = useMe();
  const orgId = me.data?.memberships[0]?.orgId;
  if (!orgId) {
    return (
      <div className="fixed inset-0 z-40 grid place-items-center bg-mist">
        <Spinner />
      </div>
    );
  }
  return <TalentDeck orgId={orgId} />;
}

function positionLabel(p: WorkerPosition): string {
  return WORKER_POSITION_LABEL_VI[p] ?? p;
}

function expPeriod(e: TalentCardExperience): string {
  const start = e.startYear ? String(e.startYear) : "";
  const end = e.current ? "nay" : e.endYear ? String(e.endYear) : "";
  if (!start && !end) return "";
  return [start, end].filter(Boolean).join(" - ");
}

/** "2 năm 3 tháng" from a month total. Empty when there's no experience. */
function fmtDuration(months: number): string {
  if (months <= 0) return "";
  const y = Math.floor(months / 12);
  const m = months % 12;
  return [y > 0 ? `${y} năm` : "", m > 0 ? `${m} tháng` : ""]
    .filter(Boolean)
    .join(" ");
}

function TalentDeck({ orgId }: { orgId: string }) {
  const nav = useNavigate();
  const { success, error } = useToast();
  const plan = usePlanStatus(orgId);
  // "Lượt mời phỏng vấn" = remaining sourcing (plan quota + à-la-carte extra).
  const available = plan.data?.sourcingRemaining ?? 0;

  const postingsQ = useOrgPostings(orgId);
  const openPostings = React.useMemo(
    () => (postingsQ.data?.items ?? []).filter((p) => p.state === "OPEN"),
    [postingsQ.data],
  );

  const feed = useTalentFeed(orgId);
  const deck = React.useMemo<TalentCard[]>(
    () => (feed.data?.pages ?? []).flatMap((p) => p.items),
    [feed.data],
  );

  const [index, setIndex] = React.useState(0);
  const current = deck[index] ?? null;

  const [postingId, setPostingId] = React.useState("");
  const [addressId, setAddressId] = React.useState("");
  const [picked, setPicked] = React.useState<Set<string>>(new Set());
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [detailWorkerId, setDetailWorkerId] = React.useState<string | null>(
    null,
  );

  // No fallback to the first posting: the employer must pick the target tin
  // themselves so they never invite into the wrong posting (req #3).
  const selectedPosting = openPostings.find((p) => p.id === postingId) ?? null;
  const effectivePostingId = selectedPosting?.id ?? "";
  const postingAddresses = selectedPosting?.addresses ?? [];
  const selectedAddress =
    postingAddresses.find((a) => a.id === addressId) ??
    postingAddresses[0] ??
    null;
  const effectiveAddressId = selectedAddress?.id ?? "";

  const createInvite = useCreateSourcedInvite(orgId);

  React.useEffect(() => {
    logAnalyticsEvent(EmployerAnalyticsEvent.talentFeedViewed, {});
  }, []);

  React.useEffect(() => {
    if (
      deck.length - index <= 3 &&
      feed.hasNextPage &&
      !feed.isFetchingNextPage
    ) {
      void feed.fetchNextPage();
    }
  }, [index, deck.length, feed.hasNextPage, feed.isFetchingNextPage, feed]);

  // Start each candidate with no posting chosen (employer picks deliberately).
  React.useEffect(() => {
    setPostingId("");
    setPicked(new Set());
  }, [current?.workerUserId]);

  React.useEffect(() => {
    setAddressId(postingAddresses[0]?.id ?? "");
    setPicked(new Set());
  }, [setAddressId, setPicked, effectivePostingId]);

  function advance() {
    setIndex((i) => i + 1);
  }

  function togglePick(id: string) {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else if (next.size < MAX_PICK) next.add(id);
      return next;
    });
  }

  function skip() {
    if (current) {
      logAnalyticsEvent(EmployerAnalyticsEvent.talentCardSkipped, {
        worker_id: current.workerUserId,
      });
    }
    advance();
  }

  // The "Mời phỏng vấn" button stays enabled; tapping it validates and nudges
  // the employer to pick a posting + slot (req #3) rather than silently doing
  // nothing.
  function tryInvite() {
    if (!effectivePostingId) {
      error("Hãy chọn tin tuyển dụng để mời ứng viên vào");
      return;
    }
    if (picked.size === 0) {
      error("Hãy chọn ít nhất 1 khung giờ phỏng vấn");
      return;
    }
    setConfirmOpen(true);
  }

  async function doInvite() {
    if (
      !current ||
      !effectivePostingId ||
      !effectiveAddressId ||
      picked.size === 0 ||
      createInvite.isPending
    ) {
      return;
    }
    try {
      await createInvite.mutateAsync({
        workerId: current.workerUserId,
        body: {
          postingId: effectivePostingId,
          orgAddressId: effectiveAddressId,
          slotIds: Array.from(picked),
        },
      });
      logAnalyticsEvent(EmployerAnalyticsEvent.applicantInvited, {
        slot_count: picked.size,
        source: "talent_deck",
        posting_id: effectivePostingId,
      });
      setConfirmOpen(false);
      success(
        "Đã gửi lời mời",
        "Lượt sẽ được hoàn lại nếu ứng viên không phản hồi trong 24 giờ.",
      );
      advance();
    } catch (e) {
      setConfirmOpen(false);
      if (e instanceof ChotcaApiError && e.code === "invite_already_exists") {
        advance();
        return;
      }
      error(e instanceof Error ? e.message : "Mời ứng viên thất bại");
    }
  }

  const loading = feed.isLoading || postingsQ.isLoading;
  const done = !feed.isLoading && (deck.length === 0 || !current);
  const noPostings = !postingsQ.isLoading && openPostings.length === 0;

  return (
    <div className="fixed inset-0 z-40 flex flex-col bg-mist">
      {/* top bar */}
      <div className="flex items-center gap-3 bg-paper px-4 pt-[max(0.75rem,env(safe-area-inset-top))] pb-3 shadow-sm">
        <button
          type="button"
          onClick={() => nav("/dashboard")}
          aria-label="Quay lại"
          className="grid h-9 w-9 place-items-center rounded-full bg-mist text-ink transition hover:bg-smoke"
        >
          <ArrowLeft size={18} />
        </button>
        <div className="min-w-0 flex-1">
          <p className="truncate text-base font-extrabold text-ink">
            Tìm ứng viên
          </p>
        </div>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-lime-light/70 px-3 py-1.5 text-sm font-semibold text-lime-dark">
          Còn {available} lượt mời
        </span>
      </div>

      <div className="flex-1 overflow-y-auto px-4 pb-[calc(7rem+env(safe-area-inset-bottom))] pt-5">
        <div className="mx-auto w-full max-w-md">
          {loading ? (
            <div className="grid place-items-center py-24">
              <Spinner />
            </div>
          ) : noPostings ? (
            <EmptyState
              title="Bạn chưa có tin tuyển dụng đang mở"
              desc="Hãy tạo một tin tuyển dụng trước khi bắt đầu tìm ứng viên. Bạn sẽ mời ứng viên vào đúng vị trí cần tuyển."
              ctaLabel="Tạo tin tuyển dụng"
              onCta={() => nav("/postings/new")}
            />
          ) : done ? (
            <EmptyState
              title="Hết ứng viên gợi ý"
              desc="Bạn đã xem hết danh sách hiện tại. Quay lại sau để xem thêm người mới."
              ctaLabel="Về trang chủ"
              onCta={() => nav("/dashboard")}
            />
          ) : current ? (
            <>
              <div className="overflow-hidden rounded-3xl border border-smoke bg-paper shadow-sm">
                {/* tappable CV preview → full detail modal */}
                <button
                  type="button"
                  onClick={() => setDetailWorkerId(current.workerUserId)}
                  className="block w-full text-left"
                >
                  <div className="flex items-end gap-3 bg-gradient-to-br from-lime-light to-lime px-5 pb-4 pt-6">
                    <Avatar
                      name={current.maskedName}
                      src={current.avatarUrl ?? undefined}
                      size="lg"
                      tone="ink"
                      className="ring-2 ring-paper/70"
                    />
                    <div className="min-w-0 pb-0.5">
                      <p className="truncate text-lg font-extrabold text-ink">
                        {current.maskedName}
                      </p>
                      <p className="truncate text-xs font-semibold text-ink/70">
                        {current.desiredPositions
                          .slice(0, 3)
                          .map(positionLabel)
                          .join(" · ") || "Đang tìm việc"}
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-col gap-3 px-5 py-4">
                    <div className="flex flex-wrap items-center gap-1.5">
                      {current.currentDistrict ? (
                        <span className="inline-flex items-center gap-1 text-xs font-semibold text-ash">
                          <MapPin size={12} />
                          {current.currentDistrict}
                        </span>
                      ) : null}
                      {current.kycVerified ? (
                        <Badge tone="lime-soft">✓ CCCD</Badge>
                      ) : null}
                      {current.ratingAvg !== null ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-mist px-2 py-0.5 text-xs font-bold text-ink">
                          <Star size={11} className="fill-current" />
                          {current.ratingAvg.toFixed(1)} ({current.ratingCount})
                        </span>
                      ) : null}
                      {current.hasMotorbike ? (
                        <Badge tone="mist">Có xe máy</Badge>
                      ) : null}
                    </div>

                    {/* Experience — the primary F&B hiring signal, on the card */}
                    <div>
                      <div className="mb-1.5 flex items-center justify-between gap-2">
                        <p className="flex items-center gap-1.5 text-xs font-bold text-ash">
                          <Briefcase size={13} /> Kinh nghiệm
                        </p>
                        {current.totalExperienceMonths > 0 ? (
                          <span className="rounded-full bg-lime-light/70 px-2 py-0.5 text-xs font-bold text-lime-dark">
                            {fmtDuration(current.totalExperienceMonths)}
                          </span>
                        ) : null}
                      </div>
                      {current.experiences.length === 0 ? (
                        <p className="text-sm text-ash">Chưa cập nhật</p>
                      ) : (
                        <ul className="flex flex-col gap-1.5">
                          {current.experiences.map((e, i) => (
                            <li key={i} className="text-sm text-ink/85">
                              <span className="font-semibold">
                                {positionLabel(e.position)}
                              </span>
                              {e.placeName ? (
                                <span className="text-ink/70">
                                  {" "}
                                  · {e.placeName}
                                </span>
                              ) : null}
                              {expPeriod(e) ? (
                                <span className="text-ash">
                                  {" "}
                                  · {expPeriod(e)}
                                </span>
                              ) : null}
                            </li>
                          ))}
                          {current.experienceCount >
                          current.experiences.length ? (
                            <li className="text-xs font-semibold text-lime-dark">
                              +
                              {current.experienceCount -
                                current.experiences.length}{" "}
                              kinh nghiệm khác
                            </li>
                          ) : null}
                        </ul>
                      )}
                    </div>

                    {/* Bio */}
                    {current.bioShort ? (
                      <div>
                        <p className="mb-1 text-xs font-bold text-ash">
                          Giới thiệu
                        </p>
                        <p className="rounded-xl bg-mist/60 px-3 py-2 text-sm italic text-ink/75">
                          “{current.bioShort}”
                        </p>
                      </div>
                    ) : null}

                    <span className="inline-flex items-center gap-1 text-xs font-bold text-lime-dark">
                      Xem hồ sơ đầy đủ <ChevronRight size={13} />
                    </span>
                  </div>
                </button>

                {/* invite controls (not part of the detail tap target) */}
                <div className="border-t border-smoke px-5 py-4">
                  <p className="mb-1.5 text-xs font-bold text-ash">
                    Mời vào tin
                  </p>
                  <Select
                    value={effectivePostingId}
                    onValueChange={setPostingId}
                    placeholder="Chọn tin tuyển dụng"
                    options={openPostings.map((p) => ({
                      value: p.id,
                      label: p.title,
                    }))}
                  />
                  {!effectivePostingId ? (
                    <p className="mt-1.5 text-xs text-ash">
                      Chọn tin để xem địa điểm và khung giờ phỏng vấn.
                    </p>
                  ) : null}

                  {postingAddresses.length > 1 ? (
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {postingAddresses.map((a) => {
                        const on = a.id === effectiveAddressId;
                        return (
                          <button
                            key={a.id}
                            type="button"
                            onClick={() => {
                              setAddressId(a.id);
                              setPicked(new Set());
                            }}
                            className={
                              "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold transition " +
                              (on
                                ? "border-lime bg-lime-light/60 text-lime-dark"
                                : "border-smoke text-ink hover:border-ink")
                            }
                          >
                            <MapPin size={12} />
                            {a.alias}
                          </button>
                        );
                      })}
                    </div>
                  ) : selectedAddress ? (
                    <p className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-ash">
                      <MapPin size={12} />
                      {selectedAddress.alias} · {selectedAddress.district}
                    </p>
                  ) : null}

                  {effectiveAddressId ? (
                    <div className="mt-3">
                      <p className="mb-2 text-xs font-bold text-ash">
                        Chọn khung giờ phỏng vấn
                      </p>
                      <SlotCalendar
                        orgId={orgId}
                        locationId={effectiveAddressId}
                        mode="pick"
                        picked={picked}
                        onTogglePick={togglePick}
                        maxPick={MAX_PICK}
                        onCreated={(slot) =>
                          setPicked((prev) => {
                            const next = new Set(prev);
                            if (next.size < MAX_PICK) next.add(slot.id);
                            return next;
                          })
                        }
                      />
                    </div>
                  ) : null}
                </div>
              </div>
            </>
          ) : null}
        </div>
      </div>

      {/* Pinned action bar — always reachable; the deck scrolls above it. */}
      {!loading && !noPostings && !done && current ? (
        <div className="absolute inset-x-0 bottom-0 flex items-center justify-center gap-6 bg-gradient-to-t from-mist via-mist/95 to-transparent px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-6">
          <FabBtn
            label="Bỏ qua"
            onClick={skip}
            className="border border-smoke bg-paper text-ash"
          >
            <X size={24} />
          </FabBtn>
          <FabBtn
            label={picked.size > 0 ? `Mời (${picked.size})` : "Mời phỏng vấn"}
            onClick={tryInvite}
            className="bg-lime text-ink"
          >
            <Check size={24} />
          </FabBtn>
        </div>
      ) : null}

      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Mời ứng viên phỏng vấn?"
        description="Xác nhận để gửi lời mời. Lời mời dùng 1 lượt và sẽ được hoàn nếu ứng viên không phản hồi."
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirmOpen(false)}>
              Huỷ
            </Button>
            <Button
              onClick={() => void doInvite()}
              disabled={createInvite.isPending}
            >
              Gửi lời mời
            </Button>
          </>
        }
      >
        <ul className="flex flex-col gap-2 text-sm text-ink">
          <li>
            Mời ứng viên này sẽ dùng <b>1 lượt mời</b>.
          </li>
          <li>
            Lời mời có hiệu lực trong <b>24 giờ</b>.
          </li>
          <li>
            Nếu sau 24 giờ ứng viên không phản hồi, <b>lượt sẽ được hoàn lại</b>{" "}
            cho bạn.
          </li>
        </ul>
        {available <= 0 ? (
          <p className="mt-3 rounded-lg bg-ember/10 px-3 py-2 text-sm font-semibold text-ember">
            Bạn đã hết lượt mời. Vui lòng nạp thêm để tiếp tục.
          </p>
        ) : null}
      </Dialog>

      <CandidateDetailModal
        orgId={orgId}
        workerId={detailWorkerId}
        onClose={() => setDetailWorkerId(null)}
      />
    </div>
  );
}

/** Full CV detail — reuses {@link ProfileBody}, the same body the applicant
 *  modal renders, fed by the talent-detail endpoint. */
function CandidateDetailModal({
  orgId,
  workerId,
  onClose,
}: {
  orgId: string;
  workerId: string | null;
  onClose: () => void;
}) {
  const t = useTranslations("applicantProfile");
  const q = useTalentProfile(orgId, workerId);
  const worker = q.data?.worker ?? null;
  return (
    <Dialog
      open={!!workerId}
      onClose={onClose}
      title={t("title")}
      description="Kinh nghiệm, kỹ năng và thông tin ứng viên đang tìm việc."
      size="lg"
    >
      {q.isLoading ? (
        <div className="flex items-center justify-center gap-2 py-12 text-ash">
          <Spinner /> <span>{t("loading")}</span>
        </div>
      ) : q.isError || !worker ? (
        <p className="py-12 text-center text-sm text-danger">
          {t("loadError")}
        </p>
      ) : (
        <ProfileBody
          data={{ worker, contact: null }}
          displayName={worker.maskedName}
          committed={false}
          t={
            t as unknown as (
              key: string,
              values?: Record<string, string | number>,
            ) => string
          }
        />
      )}
    </Dialog>
  );
}

function EmptyState({
  title,
  desc,
  ctaLabel,
  onCta,
}: {
  title: string;
  desc: string;
  ctaLabel: string;
  onCta: () => void;
}) {
  return (
    <div className="mt-10 rounded-3xl border border-dashed border-smoke bg-paper px-6 py-12 text-center">
      <p className="text-base font-bold text-ink">{title}</p>
      <p className="mt-1 text-sm text-ash">{desc}</p>
      <Button className="mt-5" onClick={onCta}>
        {ctaLabel}
      </Button>
    </div>
  );
}

function FabBtn({
  label,
  onClick,
  disabled,
  className,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-1.5">
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        aria-label={label}
        className={
          "grid h-16 w-16 place-items-center rounded-full shadow-sm transition active:scale-95 disabled:cursor-not-allowed " +
          (className ?? "")
        }
      >
        {children}
      </button>
      <span className="text-[11px] font-semibold text-ash">{label}</span>
    </div>
  );
}
